
import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

interface Step {
  id: number;
  title: string;
  duration: string;
  timestamp: number;
  description: string;
  highlights: string[];
  tradieContext: string;
  companyContext: string;
  icon: string;
}

export default function DemoWalkthrough() {
  const { ref, isVisible } = useScrollAnimation();
  const [activeStep, setActiveStep] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [viewMode, setViewMode] = useState<'tradie' | 'company'>('tradie');
  const [progress, setProgress] = useState(0);
  const [expandedTip, setExpandedTip] = useState<number | null>(null);
  const [showTranscript, setShowTranscript] = useState(false);
  const progressInterval = useRef<NodeJS.Timeout | null>(null);

  const steps: Step[] = [
    {
      id: 1,
      title: 'Snap Photos On-Site',
      duration: '45 sec',
      timestamp: 0,
      description: 'Simply take photos of the job site with your phone. Our AI instantly analyzes the scope, identifies materials, and measures the area.',
      highlights: [
        'Works offline - no internet needed on-site',
        'AI detects materials automatically',
        'Measures area from photos'
      ],
      tradieContext: 'You\'re at a customer\'s house looking at a bathroom reno. Instead of scribbling notes, just snap a few photos. The app does the rest.',
      companyContext: 'Your team can capture job details consistently across all sites. Every quote starts with the same professional process.',
      icon: 'ri-camera-line'
    },
    {
      id: 2,
      title: 'Review Smart Quote',
      duration: '60 sec',
      timestamp: 45,
      description: 'Within seconds, you get a detailed quote with real supplier prices. Adjust quantities, swap materials, or add custom items.',
      highlights: [
        'Real-time supplier pricing',
        'Drag-and-drop line items',
        'Save templates for repeat jobs'
      ],
      tradieContext: 'The quote pulls prices from Bunnings, Reece, and your preferred suppliers. No more guessing or underquoting.',
      companyContext: 'Set company-wide margins and pricing rules. Every quote follows your business standards automatically.',
      icon: 'ri-file-list-3-line'
    },
    {
      id: 3,
      title: 'Customize & Brand',
      duration: '30 sec',
      timestamp: 105,
      description: 'Add your logo, terms, and personal touch. Choose from professional templates that make you look established.',
      highlights: [
        'Upload your logo once',
        'Custom terms & conditions',
        'Professional PDF export'
      ],
      tradieContext: 'Even if you\'re a one-man band, your quotes look like they came from a big company. Customers take you seriously.',
      companyContext: 'Maintain brand consistency across all team members. Lock certain fields so quotes always meet company standards.',
      icon: 'ri-palette-line'
    },
    {
      id: 4,
      title: 'Send & Track',
      duration: '45 sec',
      timestamp: 135,
      description: 'Send quotes via email or SMS with one tap. Get notified when customers view it, and see when they\'re ready to decide.',
      highlights: [
        'Email or SMS delivery',
        'Real-time view notifications',
        'Automatic follow-up reminders'
      ],
      tradieContext: 'You\'ll know the moment Mrs. Johnson opens your quote. Follow up at the perfect time and win more jobs.',
      companyContext: 'Track all outstanding quotes in one dashboard. See which team members are closing deals and who needs help.',
      icon: 'ri-send-plane-line'
    },
    {
      id: 5,
      title: 'Get Paid Faster',
      duration: '40 sec',
      timestamp: 180,
      description: 'Convert accepted quotes to invoices instantly. Customers pay online with card or bank transfer. Money hits your account in 1-2 days.',
      highlights: [
        'One-click invoice creation',
        'Online payment options',
        'Automatic payment reminders'
      ],
      tradieContext: 'No more chasing payments. Customers pay online before you even leave the job. Cash flow sorted.',
      companyContext: 'Reconcile payments automatically. See who\'s paid, who\'s overdue, and forecast your cash flow accurately.',
      icon: 'ri-bank-card-line'
    }
  ];

  const totalDuration = 220; // Total demo duration in seconds

  // Simulate video progress
  useEffect(() => {
    if (isPlaying) {
      progressInterval.current = setInterval(() => {
        setProgress(prev => {
          const newProgress = prev + (100 / totalDuration / 10);
          if (newProgress >= 100) {
            setIsPlaying(false);
            return 100;
          }
          
          // Update active step based on progress
          const currentTime = (newProgress / 100) * totalDuration;
          const newStep = steps.findIndex((step, index) => {
            const nextStep = steps[index + 1];
            return currentTime >= step.timestamp && (!nextStep || currentTime < nextStep.timestamp);
          });
          if (newStep !== -1 && newStep !== activeStep) {
            setActiveStep(newStep);
          }
          
          return newProgress;
        });
      }, 100);
    } else {
      if (progressInterval.current) {
        clearInterval(progressInterval.current);
      }
    }
    
    return () => {
      if (progressInterval.current) {
        clearInterval(progressInterval.current);
      }
    };
  }, [isPlaying, activeStep]);

  const jumpToStep = (stepIndex: number) => {
    setActiveStep(stepIndex);
    const step = steps[stepIndex];
    setProgress((step.timestamp / totalDuration) * 100);
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const currentTime = (progress / 100) * totalDuration;

  return (
    <section ref={ref} id="demo-walkthrough" className="py-24 bg-gradient-to-b from-slate-900 via-slate-900 to-slate-800 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className={`text-center mb-12 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500/20 border border-amber-500/30 rounded-full mb-6">
            <i className="ri-play-circle-line text-amber-400"></i>
            <span className="text-amber-400 text-sm font-semibold uppercase tracking-wide">Interactive Demo</span>
          </div>
          
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4 leading-tight">
            See How It Works
            <span className="block text-amber-400">In Under 4 Minutes</span>
          </h2>
          
          <p className="text-lg text-slate-400 max-w-2xl mx-auto mb-8">
            Watch a real walkthrough of how tradies use MtaPlannerPro to quote jobs faster, 
            look more professional, and get paid on time.
          </p>

          {/* View Mode Toggle */}
          <div className="inline-flex items-center gap-1 p-1 bg-slate-800 rounded-full">
            <button
              onClick={() => setViewMode('tradie')}
              className={`px-6 py-2.5 rounded-full text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
                viewMode === 'tradie'
                  ? 'bg-amber-500 text-white shadow-lg'
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              <i className="ri-user-line mr-2"></i>
              I'm a Solo Tradie
            </button>
            <button
              onClick={() => setViewMode('company')}
              className={`px-6 py-2.5 rounded-full text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
                viewMode === 'company'
                  ? 'bg-amber-500 text-white shadow-lg'
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              <i className="ri-building-line mr-2"></i>
              I Run a Company
            </button>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-5 gap-8">
          {/* Video Player - Left Side */}
          <div className={`lg:col-span-3 transition-all duration-700 delay-100 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}>
            {/* Video Container */}
            <div className="relative rounded-2xl overflow-hidden bg-slate-800 shadow-2xl">
              {/* Video Placeholder with Animated Content */}
              <div className="aspect-video relative bg-gradient-to-br from-slate-800 to-slate-900">
                {/* Animated Demo Screen */}
                <div className="absolute inset-0 flex items-center justify-center">
                  {/* Phone Mockup */}
                  <div className="relative w-48 md:w-64 transform transition-all duration-500">
                    <div className="bg-slate-950 rounded-[2rem] p-2 shadow-2xl">
                      <div className="bg-white rounded-[1.5rem] overflow-hidden">
                        {/* Phone Screen Content */}
                        <div className="aspect-[9/16] relative">
                          {/* Dynamic content based on active step */}
                          {activeStep === 0 && (
                            <div className="absolute inset-0 bg-gradient-to-b from-amber-50 to-white p-4 flex flex-col items-center justify-center">
                              <div className={`w-16 h-16 rounded-full bg-amber-500 flex items-center justify-center mb-4 ${isPlaying ? 'animate-pulse' : ''}`}>
                                <i className="ri-camera-line text-white text-2xl"></i>
                              </div>
                              <p className="text-slate-900 font-semibold text-sm text-center">Tap to capture job site</p>
                              <div className="mt-4 w-full h-24 bg-slate-100 rounded-lg flex items-center justify-center">
                                <i className="ri-image-line text-slate-400 text-3xl"></i>
                              </div>
                            </div>
                          )}
                          {activeStep === 1 && (
                            <div className="absolute inset-0 bg-white p-3">
                              <div className="bg-amber-500 text-white text-xs font-bold px-2 py-1 rounded mb-2">QUOTE</div>
                              <div className="space-y-2">
                                {['Materials', 'Labour', 'Markup'].map((item, i) => (
                                  <div key={i} className="flex justify-between items-center p-2 bg-slate-50 rounded text-xs">
                                    <span className="text-slate-700">{item}</span>
                                    <span className="font-bold text-slate-900">${(Math.random() * 500 + 100).toFixed(0)}</span>
                                  </div>
                                ))}
                                <div className="border-t pt-2 flex justify-between items-center">
                                  <span className="font-bold text-slate-900 text-sm">Total</span>
                                  <span className="font-bold text-amber-600 text-lg">$2,450</span>
                                </div>
                              </div>
                            </div>
                          )}
                          {activeStep === 2 && (
                            <div className="absolute inset-0 bg-white p-3">
                              <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center mx-auto mb-2">
                                <i className="ri-building-2-line text-amber-600"></i>
                              </div>
                              <p className="text-center text-slate-900 font-bold text-sm mb-1">Your Logo Here</p>
                              <div className="space-y-1">
                                <div className="h-2 bg-slate-200 rounded w-full"></div>
                                <div className="h-2 bg-slate-200 rounded w-3/4"></div>
                              </div>
                              <div className="mt-3 p-2 bg-amber-50 rounded border border-amber-200">
                                <p className="text-xs text-amber-800">Professional template applied ✓</p>
                              </div>
                            </div>
                          )}
                          {activeStep === 3 && (
                            <div className="absolute inset-0 bg-white p-3 flex flex-col items-center justify-center">
                              <div className={`w-14 h-14 rounded-full bg-emerald-500 flex items-center justify-center mb-3 ${isPlaying ? 'animate-bounce' : ''}`}>
                                <i className="ri-send-plane-fill text-white text-xl"></i>
                              </div>
                              <p className="text-slate-900 font-semibold text-sm">Quote Sent!</p>
                              <p className="text-slate-500 text-xs mt-1">Customer notified via SMS</p>
                              <div className="mt-3 flex items-center gap-2 text-emerald-600 text-xs">
                                <i className="ri-eye-line"></i>
                                <span>Viewed 2 mins ago</span>
                              </div>
                            </div>
                          )}
                          {activeStep === 4 && (
                            <div className="absolute inset-0 bg-gradient-to-b from-emerald-500 to-emerald-600 p-4 flex flex-col items-center justify-center">
                              <div className="w-16 h-16 rounded-full bg-white flex items-center justify-center mb-3">
                                <i className="ri-check-line text-emerald-500 text-3xl"></i>
                              </div>
                              <p className="text-white font-bold text-lg">Payment Received!</p>
                              <p className="text-emerald-100 text-sm mt-1">$2,450.00 AUD</p>
                              <p className="text-emerald-200 text-xs mt-3">Deposited in 1-2 business days</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    {/* Phone Notch */}
                    <div className="absolute top-4 left-1/2 -translate-x-1/2 w-20 h-5 bg-slate-950 rounded-full"></div>
                  </div>
                </div>

                {/* Step Indicator Overlay */}
                <div className="absolute top-4 left-4 bg-black/60 backdrop-blur-sm rounded-lg px-3 py-2">
                  <p className="text-white text-sm font-medium">Step {activeStep + 1} of {steps.length}</p>
                  <p className="text-amber-400 text-xs">{steps[activeStep].title}</p>
                </div>

                {/* Time Overlay */}
                <div className="absolute top-4 right-4 bg-black/60 backdrop-blur-sm rounded-lg px-3 py-2">
                  <p className="text-white text-sm font-mono">{formatTime(currentTime)} / {formatTime(totalDuration)}</p>
                </div>

                {/* Play Button Overlay */}
                {!isPlaying && (
                  <button
                    onClick={() => setIsPlaying(true)}
                    className="absolute inset-0 flex items-center justify-center bg-black/30 hover:bg-black/40 transition-colors cursor-pointer group"
                  >
                    <div className="w-20 h-20 rounded-full bg-amber-500 flex items-center justify-center shadow-2xl group-hover:scale-110 transition-transform">
                      <i className="ri-play-fill text-white text-4xl ml-1"></i>
                    </div>
                  </button>
                )}
              </div>

              {/* Video Controls */}
              <div className="bg-slate-950 px-4 py-3">
                <div className="flex items-center gap-4">
                  {/* Play/Pause */}
                  <button
                    onClick={() => setIsPlaying(!isPlaying)}
                    className="w-10 h-10 rounded-full bg-amber-500 flex items-center justify-center hover:bg-amber-400 transition-colors cursor-pointer"
                  >
                    <i className={`${isPlaying ? 'ri-pause-fill' : 'ri-play-fill'} text-white text-lg`}></i>
                  </button>

                  {/* Progress Bar */}
                  <div className="flex-1 relative">
                    <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-amber-500 to-orange-500 transition-all duration-100"
                        style={{ width: `${progress}%` }}
                      ></div>
                    </div>
                    {/* Chapter Markers */}
                    <div className="absolute inset-0 flex items-center">
                      {steps.map((step, index) => (
                        <button
                          key={step.id}
                          onClick={() => jumpToStep(index)}
                          className="absolute w-3 h-3 rounded-full bg-white border-2 border-amber-500 hover:scale-125 transition-transform cursor-pointer"
                          style={{ left: `${(step.timestamp / totalDuration) * 100}%` }}
                          title={step.title}
                        ></button>
                      ))}
                    </div>
                  </div>

                  {/* Transcript Toggle */}
                  <button
                    onClick={() => setShowTranscript(!showTranscript)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                      showTranscript ? 'bg-amber-500 text-white' : 'bg-slate-800 text-slate-400 hover:text-white'
                    }`}
                  >
                    <i className="ri-closed-captioning-line mr-1"></i>
                    CC
                  </button>
                </div>

                {/* Transcript */}
                {showTranscript && (
                  <div className="mt-3 p-3 bg-slate-800 rounded-lg">
                    <p className="text-white text-sm leading-relaxed">
                      {viewMode === 'tradie' ? steps[activeStep].tradieContext : steps[activeStep].companyContext}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Narrator Card */}
            <div className="mt-6 bg-slate-800/50 rounded-xl p-4 flex items-center gap-4 border border-slate-700">
              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center flex-shrink-0">
                <i className="ri-user-voice-line text-white text-xl"></i>
              </div>
              <div className="flex-1">
                <p className="text-white font-semibold">Mike Thompson</p>
                <p className="text-slate-400 text-sm">Master Electrician • 15+ years experience</p>
              </div>
              <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-500/20 rounded-full">
                <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></span>
                <span className="text-emerald-400 text-xs font-medium">Verified Tradie</span>
              </div>
            </div>
          </div>

          {/* Step Guide - Right Side */}
          <div className={`lg:col-span-2 transition-all duration-700 delay-200 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`}>
            <div className="bg-slate-800/30 rounded-2xl p-6 border border-slate-700/50 sticky top-24">
              <h3 className="text-white font-bold text-lg mb-6 flex items-center gap-2">
                <i className="ri-list-check-2 text-amber-400"></i>
                Step-by-Step Guide
              </h3>

              {/* Steps List */}
              <div className="space-y-4">
                {steps.map((step, index) => (
                  <div key={step.id}>
                    <button
                      onClick={() => jumpToStep(index)}
                      className={`w-full text-left p-4 rounded-xl transition-all cursor-pointer ${
                        activeStep === index
                          ? 'bg-amber-500/20 border border-amber-500/50'
                          : 'bg-slate-800/50 border border-transparent hover:border-slate-600'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        {/* Step Number/Icon */}
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                          activeStep === index
                            ? 'bg-amber-500 text-white'
                            : index < activeStep
                              ? 'bg-emerald-500 text-white'
                              : 'bg-slate-700 text-slate-400'
                        }`}>
                          {index < activeStep ? (
                            <i className="ri-check-line text-lg"></i>
                          ) : (
                            <i className={`${step.icon} text-lg`}></i>
                          )}
                        </div>

                        {/* Step Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2 mb-1">
                            <h4 className={`font-semibold truncate ${activeStep === index ? 'text-amber-400' : 'text-white'}`}>
                              {step.title}
                            </h4>
                            <span className="text-slate-500 text-xs whitespace-nowrap">{step.duration}</span>
                          </div>
                          
                          {activeStep === index && (
                            <div className="mt-2 space-y-2 animate-fadeIn">
                              <p className="text-slate-400 text-sm leading-relaxed">
                                {step.description}
                              </p>
                              
                              {/* Highlights */}
                              <ul className="space-y-1">
                                {step.highlights.map((highlight, i) => (
                                  <li key={i} className="flex items-center gap-2 text-sm">
                                    <i className="ri-check-line text-emerald-400 text-xs"></i>
                                    <span className="text-slate-300">{highlight}</span>
                                  </li>
                                ))}
                              </ul>

                              {/* Context for selected view mode */}
                              <div className="mt-3 p-3 bg-slate-900/50 rounded-lg border-l-2 border-amber-500">
                                <p className="text-amber-200 text-xs font-medium mb-1">
                                  {viewMode === 'tradie' ? '👷 For You as a Tradie:' : '🏢 For Your Company:'}
                                </p>
                                <p className="text-slate-300 text-sm italic">
                                  "{viewMode === 'tradie' ? step.tradieContext : step.companyContext}"
                                </p>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </button>

                    {/* Pro Tip Accordion */}
                    {activeStep === index && (
                      <button
                        onClick={() => setExpandedTip(expandedTip === index ? null : index)}
                        className="w-full mt-2 p-3 bg-amber-500/10 rounded-lg text-left cursor-pointer hover:bg-amber-500/20 transition-colors"
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-amber-400 text-sm font-medium flex items-center gap-2">
                            <i className="ri-lightbulb-line"></i>
                            Pro Tip
                          </span>
                          <i className={`ri-arrow-down-s-line text-amber-400 transition-transform ${expandedTip === index ? 'rotate-180' : ''}`}></i>
                        </div>
                        {expandedTip === index && (
                          <p className="mt-2 text-slate-300 text-sm">
                            {index === 0 && "Take photos from multiple angles. The more detail, the more accurate your quote."}
                            {index === 1 && "Save your most common jobs as templates. One tap to create repeat quotes."}
                            {index === 2 && "Add a personal message to each quote. Customers love the personal touch."}
                            {index === 3 && "Follow up within 24 hours of them viewing. That's when they're most likely to say yes."}
                            {index === 4 && "Offer a small discount for same-day payment. Most customers will take it."}
                          </p>
                        )}
                      </button>
                    )}
                  </div>
                ))}
              </div>

              {/* Bottom CTA */}
              <div className="mt-6 pt-6 border-t border-slate-700">
                <Link
                  to="/demo"
                  className="w-full py-4 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-xl font-semibold text-center flex items-center justify-center gap-2 hover:shadow-lg hover:shadow-amber-500/30 hover:-translate-y-0.5 transition-all cursor-pointer whitespace-nowrap"
                >
                  Try It Yourself
                  <i className="ri-arrow-right-line"></i>
                </Link>
                <p className="text-center text-slate-500 text-xs mt-3">
                  Free 14-day trial • No credit card required
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Stats */}
        <div className={`mt-16 grid grid-cols-2 md:grid-cols-4 gap-4 transition-all duration-700 delay-300 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          {[
            { icon: 'ri-time-line', value: '3:40', label: 'Demo Length', sublabel: 'Quick overview' },
            { icon: 'ri-user-star-line', value: '94%', label: 'Found Helpful', sublabel: 'User feedback' },
            { icon: 'ri-question-answer-line', value: '24/7', label: 'Support Available', sublabel: 'Always here' },
            { icon: 'ri-shield-check-line', value: '100%', label: 'Risk Free', sublabel: '30-day guarantee' },
          ].map((stat, i) => (
            <div key={i} className="bg-slate-800/50 rounded-xl p-4 text-center border border-slate-700/50">
              <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center mx-auto mb-2">
                <i className={`${stat.icon} text-amber-400`}></i>
              </div>
              <p className="text-2xl font-bold text-white">{stat.value}</p>
              <p className="text-slate-400 text-sm">{stat.label}</p>
              <p className="text-slate-500 text-xs">{stat.sublabel}</p>
            </div>
          ))}
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
      `}</style>
    </section>
  );
}
